import React, { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useKnowledgeSourceManagement } from './hooks/useKnowledgeSourceManagement';
import { handleSourceActivation } from './utils';
import { ACTION_TYPES } from './constants';
import {
  SourceTableHeader,
  SourceTable,
  SourceModals,
  SourceBreadcrumbs,
  SourcePagination,
} from './components';
import type { KnowledgeSourceItem } from '../../../../api';
import type { ActionType, ExtendedActionCardPayload } from './types';

export const KnowledgeSourceList: React.FC = () => {
  const { params } = useRouteMatch<{ id: string }>();

  const {
    loading,
    sort,
    setSort,
    isOpened,
    filters,
    onOpen,
    onClose,
    setPage,
    setFilters,
    dataSources,
    handleUploadSourceFile,
    handleUploadSourceText,
    isSourceCreating,
    handleActivateKnowledgeSource,
    activatingKnowledgeSource,
    isOpenedRenameModal,
    onCloseRenameModal,
    onOpenRenameModal,
    handleRenameKnowledgeSource,
    renamingKnowledgeSource,
    handleDeleteKnowledgeSource,
    deletingKnowledgeSource,
    handleToggleActiveKnowledgeSource,
    isOpenedDeleteModal,
    onCloseDeleteModal,
    onOpenDeleteModal,
    isOpenedActivateModal,
    onCloseActivateModal,
    onOpenActivateModal,
    handleUploadSourceFirecrawl,
    isOpenedSourceModal,
    onCloseSourceModal,
    onOpenSourceModal,
    isOpenedEditChunkModal,
    onCloseEditChunkModal,
    updatingChunk,
    editingChunk,
    handleEditChunk,
    handleSaveEditChunk,
    refreshTrigger,
    knowledgeBaseData,
    pagination,
    handleDownloadKnowledgeSource,
  } = useKnowledgeSourceManagement(params.id);

  const [actionPayload, setActionPayload] =
    useState<ExtendedActionCardPayload | null>(null);

  const handleActivateSource = (
    id: string,
    vectorized: boolean = false,
    fileType?: string
  ) => {
    handleSourceActivation(id, vectorized, fileType, {
      onToggleActive: handleToggleActiveKnowledgeSource,
      onActivateKnowledgeSource: handleActivateKnowledgeSource,
      onOpenActivateModal,
      onSetActionPayload: setActionPayload,
    });
  };

  const handleSourceAction = (
    action: ActionType,
    payload: ExtendedActionCardPayload
  ) => {
    setActionPayload(payload);

    switch (action) {
      case ACTION_TYPES.RENAME:
        onOpenRenameModal();
        break;
      case ACTION_TYPES.DELETE:
        onOpenDeleteModal();
        break;
      case ACTION_TYPES.DETAILS:
        onOpenSourceModal();
        break;
      case ACTION_TYPES.ACTIVATE:
        onOpenActivateModal();
        break;
      case ACTION_TYPES.DEACTIVATE:
        if (payload.id) {
          handleToggleActiveKnowledgeSource(payload.id, false);
        }
        break;
      case ACTION_TYPES.DOWNLOAD:
        if (payload.id && payload.name) {
          handleDownloadKnowledgeSource(payload.id);
        }
        break;
      default:
        break;
    }
  };

  const handleRagSearch = () => {
    onOpenSourceModal();
  };

  const handleAddDataSource = () => {
    onOpen();
  };

  const handleCloseSourceModal = () => {
    setActionPayload(null);
    onCloseSourceModal();
  };

  return (
    <div>
      <SourceBreadcrumbs
        knowledgeBaseName={knowledgeBaseData?.name}
        knowledgeBaseId={params.id}
      />

      <SourceTableHeader
        filters={filters}
        onFiltersChange={setFilters}
        onRagSearch={handleRagSearch}
        onAddDataSource={handleAddDataSource}
      />
      <SourceModals
        isOpened={isOpened}
        isOpenedRenameModal={isOpenedRenameModal}
        isOpenedDeleteModal={isOpenedDeleteModal}
        isOpenedActivateModal={isOpenedActivateModal}
        isOpenedSourceModal={isOpenedSourceModal}
        isOpenedEditChunkModal={isOpenedEditChunkModal}
        onClose={onClose}
        onCloseRenameModal={onCloseRenameModal}
        onCloseDeleteModal={onCloseDeleteModal}
        onCloseActivateModal={onCloseActivateModal}
        onCloseSourceModal={handleCloseSourceModal}
        onCloseEditChunkModal={onCloseEditChunkModal}
        actionPayload={actionPayload}
        setActionPayload={setActionPayload}
        editingChunk={editingChunk}
        refreshTrigger={refreshTrigger}
        isSourceCreating={isSourceCreating}
        deletingKnowledgeSource={deletingKnowledgeSource}
        renamingKnowledgeSource={renamingKnowledgeSource}
        activatingKnowledgeSource={activatingKnowledgeSource}
        updatingChunk={updatingChunk}
        handleUploadSourceFile={handleUploadSourceFile}
        handleUploadSourceText={handleUploadSourceText}
        handleUploadSourceFirecrawl={handleUploadSourceFirecrawl}
        handleRenameKnowledgeSource={handleRenameKnowledgeSource}
        handleDeleteKnowledgeSource={handleDeleteKnowledgeSource}
        handleActivateKnowledgeSource={handleActivateKnowledgeSource}
        handleSaveEditChunk={handleSaveEditChunk}
        handleEditChunk={handleEditChunk}
      />

      <SourceTable
        data={dataSources as KnowledgeSourceItem[]}
        loading={loading}
        sort={sort}
        onSort={setSort}
        onActivateSource={handleActivateSource}
        onSourceAction={handleSourceAction}
      />

      <SourcePagination
        totalItems={pagination?.totalItems}
        page={pagination?.page}
        onPageChange={setPage}
      />
    </div>
  );
};

export default KnowledgeSourceList;
